import { createFileRoute } from "@tanstack/react-router"

import { pageHead } from "@/lib/metadata"
import Link from "@/components/link"

import {
  LegalBody,
  LegalList,
  LegalSection,
} from "@/components/layout/legal-prose"
import { PageHeader, SiteShell } from "@/components/layout/site-shell"
import { siteConfig } from "@/lib/site"

function TermsPage() {
  return (
    <SiteShell>
      <PageHeader title="Terms of use">
        <p>
          These terms cover the {siteConfig.name} website, the account you
          create here and use to sign in to {siteConfig.name}’s apps, and
          anything you share through it.
        </p>
      </PageHeader>

      <LegalBody updated={siteConfig.contentUpdated}>
        <LegalSection title="Agreement">
          <p>
            By using this site you accept these terms. If you do not accept
            them, do not use the site. {siteConfig.name} provides this website
            as the home of your {siteConfig.name} account: sign-in, plans and
            billing, credits, and support for {siteConfig.name}’s apps. The apps
            themselves may run on their own sites or on your device, and may
            come with additional terms of their own.
          </p>
          <p>
            The service is operated by {siteConfig.legalName}, trading as{" "}
            {siteConfig.name}.
            {siteConfig.businessAddress && (
              <> Our business address is {siteConfig.businessAddress}.</>
            )}
          </p>
        </LegalSection>

        <LegalSection title="Using the site">
          <p>
            Use the site lawfully and as intended. Do not scrape it at scale,
            reverse-engineer it, probe it for vulnerabilities, interfere with
            it, or use it to send spam or automated bulk messages. We may
            rate-limit, block, or remove access that harms the service or
            others.
          </p>
        </LegalSection>

        <LegalSection title="Your account">
          <p>
            Your {siteConfig.name} account signs you in to this site and to{" "}
            {siteConfig.name}’s apps, and it is where your plan, credits and
            support tickets live. When you create one:
          </p>
          <LegalList
            items={[
              {
                term: "Give real details",
                description:
                  "Use an email address you control and can read. Do not impersonate anyone.",
              },
              {
                term: "Keep it secure",
                description:
                  "You are responsible for what is sent from your account. Tell us if you think someone else has access to it.",
              },
              {
                term: "Do not evade limits",
                description:
                  "Do not create accounts to get around a limit we have applied.",
              },
            ]}
          />
          <p>
            You may close your account from the account menu or contact us for
            help. Closing it deletes the data linked to that account, including
            its support tickets and launch-list signup, and cancels any paid
            plan, as described in our{" "}
            <Link href="/privacy">privacy notice</Link>. We may suspend or close
            an account that breaks these terms.
          </p>
        </LegalSection>

        <LegalSection title="Content you share">
          <p>
            Anything you share through the site, including messages, company
            details, or links in a message, must be accurate, yours to share,
            and lawful. Do not use the service for unlawful, threatening,
            abusive, deceptive, infringing, bulk, or automated sending.
          </p>
          <p>
            We record a ticket submission with the account and IP address it
            came from. We may report a threatening, abusive, or unlawful
            submission to the relevant authorities with that record. What we
            hold and for how long is described in our{" "}
            <Link href="/privacy">privacy notice</Link>.
          </p>
          <p>
            You keep the rights to what you share. You give us permission to
            store and read it to operate the service and answer you.
          </p>
        </LegalSection>

        <LegalSection title="Our content">
          <p>
            The site, its text, design, and code are ours or our licensors’. You
            may read and share it, but may not copy it wholesale, republish it
            as your own, or remove notices. Our name and logo remain ours.
          </p>
        </LegalSection>

        <LegalSection title="Paid plans">
          <p>
            Paid plans are sold by our reseller and merchant of record, Polar
            (polar.sh), which processes payment, charges any sales tax or VAT,
            and issues invoices under its own terms. The price, billing
            interval, and any trial are shown at checkout before you pay.
          </p>
          <p>
            Subscriptions renew automatically at the end of each period until
            cancelled. You can cancel at any time from the billing page in your
            dashboard; you keep access until the end of the period already paid
            for. Closing your account cancels any subscription immediately.
          </p>
        </LegalSection>

        <LegalSection title="Team plan">
          <p>
            A Team subscription covers the person who pays (the owner) and the
            people they invite, up to the number of seats shown on the pricing
            page. Each person on a team gets the Team plan’s credit allowance
            for themselves; it is not shared. An account can hold a seat on one
            team at a time, and an invited person accepts by signing in with the
            address that was invited.
          </p>
          <p>
            A seat lasts only while the owner’s own plan is Team. If the
            subscription ends, or the owner closes their account, the seats stop
            working, and they work again if the owner resubscribes. People can
            leave a team at any time, and the owner can remove anyone. The owner
            sees the names and email addresses of the people on their team, and
            they see the owner’s.
          </p>
          <p>
            Invite only people who expect it. Invitations are emails sent from
            us, and we may limit or remove invitations used to send unwanted
            email.
          </p>
        </LegalSection>

        <LegalSection title="Anysola credits" id="credits">
          <p>
            A paid plan is a membership of Anysola’s apps, not of one app. Each
            plan includes an allowance of Anysola credits every month, which the
            apps you sign in to with your account spend as you use them. The
            allowance for each plan is shown on the pricing page.
          </p>
          <LegalList
            items={[
              {
                term: "Not money",
                description:
                  "Credits have no cash value. They cannot be sold, transferred to another account, exchanged, or refunded, including when a plan ends or an account is closed.",
              },
              {
                term: "Monthly, no carry-over",
                description:
                  "The allowance refreshes each month on your billing date (on the 1st of the month if you are not billed) and unused allowance does not carry over. Bonus credits we give you separately do not expire, but are spent after the allowance.",
              },
              {
                term: "Exceptions",
                description:
                  "Not every app or feature is included in every plan, and some may use credits at a different rate or not accept them at all. Where that applies, the app or the pricing page says so before you spend.",
              },
              {
                term: "Changes",
                description:
                  "We may change allowances, what an action costs in credits, and which apps accept them. We will give notice in the service before a change that reduces your plan’s allowance takes effect.",
              },
            ]}
          />
          <p>
            We may remove credits obtained through a fault, abuse, or a breach
            of these terms, and credits spent that way do not have to be
            restored.
          </p>
        </LegalSection>

        <LegalSection title="Availability and liability">
          <p>
            We run the site with care but do not guarantee that it will always
            be available, unchanged, or error-free. It is provided as is,
            without warranties to the extent the law allows. Site content is
            information, not professional advice.
          </p>
          <p>
            Contacting us does not by itself create a contract and does not
            oblige us to reply. To the extent the law allows, we are not liable
            for indirect or consequential loss, lost profit, or lost data.
            Nothing here limits liability that cannot legally be limited.
          </p>
        </LegalSection>

        <LegalSection title="Third-party services">
          <p>
            The site relies on third parties for authentication, payments, email
            delivery, storage, and analytics. An outage at one of them can
            affect part of the service. The providers and their roles are listed
            in our <Link href="/privacy">privacy notice</Link>.
          </p>
        </LegalSection>

        <LegalSection title="Changes and contact">
          <p>
            We may update these terms; the date at the top says when we last
            did. Continuing to use the site after a change means you accept the
            revised version. We may provide notice in the service when a change
            materially affects you.
          </p>
          <p>
            These terms do not limit rights that cannot be excluded under the
            law that applies to you. If any part is unenforceable, the rest
            remains in effect. Questions go to{" "}
            <a href={`mailto:${siteConfig.contactEmail}`}>
              {siteConfig.contactEmail}
            </a>
            .
          </p>
          {siteConfig.governingLaw && (
            <p>
              These terms are governed by {siteConfig.governingLaw}, except
              where mandatory law gives you rights that cannot be excluded.
            </p>
          )}
        </LegalSection>
      </LegalBody>
    </SiteShell>
  )
}

export const Route = createFileRoute("/terms")({
  head: () =>
    pageHead({
      title: "Terms",
      description: "The terms for using Anysola and its paid plans.",
      path: "/terms",
      image: "/terms/opengraph-image",
    }),
  component: TermsPage,
})
